import React from 'react';
import { FieldValue, RegisterOptions, Validate } from 'react-hook-form';
import FormInputValidator from '@app/component/form/input/FormInputValidator';
import has from 'lodash/has';
import get from 'lodash/get';
import FormInputStyleProperties from '@app/component/form/input/FormInputStyleProperties';
import { useTranslation } from '@app/component/translation/Translation';
import Utilities from '@util/Utilities';
import _uniqueId from 'lodash/uniqueId';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';

type Props = {
	placeholder?: string,
	form: any,
	name: string,
	label?: string,
	labelHint?: string,
	disabled?: boolean,
	className?: string,
	extraClassName?: string,
	hideErrorMessage?: boolean,
	validation?: FormInputValidator | Validate<any, FieldValue<any>>,
} & FormInputStyleProperties

const FormInputPassword: React.FunctionComponent<Props> = (
	{
		form,
		placeholder,
		name,
		label,
		isLoading,
		disabled = false,
		className,
		extraClassName,
		labelHint,
		hideErrorMessage = false,
		validation,
	}) => {

	const { t } = useTranslation('common.form.error');
	const hasError = !!form && has(form.formState.errors, name);
	const errorMessage = hasError ? (get(form.formState.errors, name + '.message') || t('required')) : '';
	const [type, setType] = React.useState('password');

	const uniqueId = _uniqueId('input-');

	const options: Partial<RegisterOptions> = {
		required: true,
	};
	if (!!validation) {
		if (typeof validation === 'function') {
			options.validate = validation;
		} else if (typeof validation === 'object') {
			options.validate = {
				checkUrl: (value) => {
					return (validation as FormInputValidator).check(value);
				},
			};
		}
	}

	const renderField = () => {
		return <>
			<input id={uniqueId} type={type}
						 className={Utilities.classNames(
							 !!className ? className : 'block input min-w-0 grow pr-3 text-base sm:text-sm/6',
							 Utilities.NVL(hasError, 'input-error'),
							 Utilities.NVL(isLoading, 'input-isLoading'),
							 Utilities.NVL(disabled, 'input-disabled'),
							 Utilities.NVL(!!extraClassName, extraClassName!),
						 )}
						 disabled={disabled}
						 placeholder={placeholder} {...form.register(name, options)} />

		</>;
	};

	const renderEye = () => {
		return <a className="absolute inset-y-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-hidden focus:text-blue-600 hover:text-blue-600" onClick={() => setType(type === 'password' ? 'text' : 'password')}>
			{type == 'password' ? <EyeIcon className="w-5 h-5"/> : <EyeSlashIcon className="w-5 h-5"/>}
		</a>
	}

	return !!label ? <div>
		<div className="flex justify-between">
			<div className="label">{label}</div>
			{!!labelHint ? <span className="text-xs leading-6 text-gray-500">{labelHint}</span> : ''}
		</div>
		<div className="mt-0.5">
			<div className="relative">
				{renderField()}
				{renderEye()}
			</div>
		</div>
		{!!errorMessage && !hideErrorMessage && <p className="help help-error">{errorMessage}</p>}
	</div> : renderField();

};

export default FormInputPassword;